<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="import-main">
    <div class="top-section">
      <div class="file-input">
        <div style="display: flex">
          <Input
            textType="file"
            placeholder="Select File"
            :inputValue.sync="importDoc['docName']"
            @fileChanged="handleImportData"
            style="width: fit-content; margin-right: 5px"
          />
          <button @click="uploadImportClicked" :disabled="disableImportButton" class="import-button"
            >Import Document</button
          >
        </div>
        <div style="min-inline-size: max-content; margin-right: 5px">
          <div
            ><img
              src="https://dsmlib.logistics.systems/Icons/warning.png"
              alt="Duplicate"
              width="30px "
              style="margin-left: 20px"
            />
            Duplicate</div
          >
          <div
            ><img
              src="https://dsmlib.logistics.systems/Icons/error.png"
              alt="Error"
              width="30px"
              style="margin-left: 20px"
            />
            Error</div
          >
        </div>
        <div class="message-container" :style="messageBoxStyle" v-html="messageBoxText"></div>
      </div>
    </div>
    <div class="bottom-section">
      <div class="table-container" style="margin-right: 10px">
        <TableBox
          title="Uploaded Data"
          :tableData="uploadedData"
          v-if="showImportDataTable"
          :loading="!showImportDataTable"
          style="height: 100%; overflow: scroll"
          groupBy="Invoice #"
          :additionalIconsIndex="uploadedData.length > 0 && uploadedData[0][''] ? [0] : []"
        />
      </div>
      <div class="table-container">
        <TableBox
          title="Imported Successfully"
          v-if="showSuccessInv"
          :tableData="importSuccessInv"
          :additionalButtons="importSuccessInvTableBtn"
          @buttonClicked="importSuccessButtonClicked"
          style="height: 100%; overflow: scroll"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    disableImportButton: function () {
      return this.importDoc['docName'] == '' || this.messageBoxType == 'error';
    },
    messageBoxStyle: function () {
      const style = {
        width: 'calc(100% - 550px)',
        backgroundColor: 'rgba(255, 246, 121, 0.4)',
      };
      switch (this.messageBoxType) {
        case 'success':
          style['backgroundColor'] = 'rgba(50, 205, 50, 0.4)';
          break;

        case 'import error':
        case 'error':
          style['backgroundColor'] = 'rgba(205, 92, 92, 0.4)';
          break;

        default:
          style['backgroundColor'] = 'rgba(255, 246, 121, 0.4)';
          break;
      }
      return style;
    },
  },
  data: function () {
    return {
      importDoc: {
        docName: '',
        attachmentFile: [],
        filetype: '',
        docGuid: '',
        remarks: '',
        docID: 0,
        original: [],
      },
      uploadedData: [],
      showImportDataTable: true,
      importDataHeader: [],
      showSuccessInv: true,
      importSuccessInv: [],
      importSuccessInvTableBtn: [
        { text: 'Edit', index: 0, width: 60 },
        { text: 'View', index: 1, width: 60 },
      ],
      messageBoxTextList: [],
      messageBoxText: '',
      messageBoxType: '',
      possibleInvHeader: ['Invoice #', 'Invoice No', 'Invoice No.', 'Invoice'],
      invTableHeader: '',
      jobTypes: {
        invoice: [],
        debit: [],
        credit: [],
      },
      sstCode6: '',
    };
  },
  props: {},
  methods: {
    showMessage: function (type, text) {
      if (text) {
        this.messageBoxTextList.push(text);
      }
      this.messageBoxText = this.messageBoxTextList.join('<br>') || '';
      this.messageBoxType = type || '';
    },

    clearData: function () {
      this.uploadedData = [];
      this.invTableHeader = '';
      this.importDataHeader = [];
      this.showMessage();
    },

    handleImportData: async function (file) {
      this.showMessage();
      this.messageBoxTextList = [];
      this.$fullscreenLoading('Uploading File');
      this.showImportDataTable = false;
      this.clearData();
      if (file.length == 0) {
        this.importDoc['docName'] = '';
        this.importDoc['filetype'] = '';
        this.importDoc['attachmentFile'] = [];
      } else {
        this.importDoc['docName'] = file[0].name;
        this.importDoc['filetype'] = `.${file[0].name.split('.').at(-1)}`.toLowerCase();
        this.importDoc['attachmentFile'] = await file[0].toBase64(true);
        this.importDoc['original'] = file[0];
        if (!['.txt', '.xlsx', '.xls', '.csv'].includes(this.importDoc['filetype'])) {
          this.showMessage('error', 'Invalid file format. Please provide Excel (.xls/.xlsx), Text (.txt), or CSV (.csv) file only.');
          this.showImportDataTable = true;
          this.$fullscreenLoading();
          return;
        }

        let processedData = [];
        if (['.txt', '.csv'].includes(this.importDoc['filetype'])) {
          processedData = this.convertTxtToObj(this.importDoc);
        } else {
          const excel = await this.$excelToJson(this.importDoc.original, false);
          if (!excel.success) {
            this.showMessage('error', 'Please double check your excel file to make sure that it is the right format.');
            this.showImportDataTable = true;
            this.$fullscreenLoading();
            return;
          }
          let temp = [];
          excel.data.forEach((d) => (temp = temp.concat(d.data)));
          if (temp.length == 0) {
            this.showMessage('error', 'Excel file is empty. No data found.');
            this.showImportDataTable = true;
            this.$fullscreenLoading();
            return;
          }
          this.importDataHeader = Object.keys(temp[0]);
          processedData = temp;
        }
        const dataHeader = this.importDataHeader.map((h) => h.toUpperCase());
        this.invTableHeader = this.possibleInvHeader.find((h) => dataHeader.includes(h.toUpperCase()));
        if (!this.invTableHeader) {
          this.showMessage('error', 'Invoice # not found. Unable to import data');
          this.uploadedData = [];
        } else {
          this.uploadedData = processedData.filter((d) => d['Item Number'] != '');
        }

        this.$nextTick(() => {
          this.showImportDataTable = true;
        });
      }
      this.$fullscreenLoading();
    },

    convertTxtToObj: function (file) {
      const decode = window.atob(file.attachmentFile);
      const rows = decode.split('\n').filter(Boolean);
      let commaCount = 0;
      let tabCount = 0;

      for (let i = 0; i < Math.min(rows.length, 5); i++) {
        const line = rows[i];
        commaCount += (line.match(/,/g) || []).length;
        tabCount += (line.match(/\t/g) || []).length;
      }

      const headers = (tabCount > commaCount ? rows[0].split('\t') : rows[0].split(',')).map((header) => header.trim());
      const data = rows.slice(1).map((row) => {
        const values = tabCount > commaCount ? row.split('\t') : this.parseCommaDelimiter(row);
        const obj = {};

        headers.forEach((header, index) => {
          obj[header] = values[index] || '';
        });

        return obj;
      });

      this.importDataHeader = headers;
      return data;
    },

    parseCommaDelimiter: function (row) {
      const columns = [];
      let currentField = '';
      let insideQuotes = false;

      for (let i = 0; i < row.length; i++) {
        const char = row[i];

        if (char === '"' && (i === 0 || row[i - 1] !== '\\')) {
          insideQuotes = !insideQuotes;
          // comma inside quotes is not a delimiter
        } else if (char === ',' && !insideQuotes) {
          // If not inside quotes, it's a delimiter; push the field
          columns.push(currentField.trim());
          currentField = '';
        } else {
          // else add the character to the current field
          currentField += char;
        }
      }

      // Add the last columns value
      if (currentField !== '') {
        columns.push(currentField.trim());
      }

      return columns;
    },

    uploadImportClicked: async function () {
      this.messageBoxText = '';
      this.messageBoxTextList = [];
      if (this.uploadedData.length == 0) {
        this.$showAlert('error', 'No data loaded', "Click 'Load Data' to import data from the selected file.");
        return;
      }
      this.$fullscreenLoading('Importing Data');
      let allData = this.groupImportInvoice();
      const invGeneralDet = allData.map((d) => {
        return {
          'Invoice No': d['arInvNo'],
          'Invoice Date': d['invDate'],
          'Billing Customer': d['coName'],
        };
      });
      const existingInv = [];
      const overwriteInv = [];

      for (const inv of invGeneralDet) {
        const exist = await this.$apiCallV2('/BatchImport/CheckFINARDocExist', {
          doctype: inv['Invoice No'].substring(0, 2).toUpperCase(),
          invno: inv['Invoice No'],
        });
        if (exist[0]['Results'] == '1') {
          existingInv.push(inv);
        } else if (exist[0]['Results'] == '2') {
          overwriteInv.push(inv);
        }
      }

      if (existingInv.length > 0 || overwriteInv.length > 0) {
        let warningMsg = '';
        if (existingInv.length > 0) {
          warningMsg = `The following invoices will be ignored. Continue?`;
          let tableString = '';
          tableString =
            '<table style="width: 100%" border="1"><tr><th>Invoice #</th><th>Invoice Date</th><th>Billing Customer</th></tr>';
          existingInv.forEach((inv) => {
            tableString = `${tableString}<tr><td>${inv['Invoice No']}</td><td>${inv['Invoice Date']}</td><td>${inv['Billing Customer']}</td></tr>`;
          });
          tableString = `${tableString}</table>`;
          warningMsg = `${warningMsg}\n${tableString}`;
        }
        if (overwriteInv.length > 0) {
          warningMsg = `${warningMsg == '' ? '' : `\n${warningMsg}`}The following invoices will be overwritten. Continue?`;
          let tableString = '';
          tableString =
            '<table style="width: 100%" border="1"><tr><th>Invoice #</th><th>Invoice Date</th><th>Billing Customer</th></tr>';
          overwriteInv.forEach((inv) => {
            tableString = `${tableString}<tr><td>${inv['Invoice No']}</td><td>${inv['Invoice Date']}</td><td>${inv['Billing Customer']}</td></tr>`;
          });
          tableString = `${tableString}</table>`;
          warningMsg = `${warningMsg}\n${tableString}`;
        }

        const buttonOptions = [
          { text: 'Continue', color: 'blue', textColor: 'white' },
          { text: 'Cancel', color: 'gray', textColor: 'white' },
        ];
        const continueSave = await this.$showAlert(
          'warning',
          `${existingInv.length + overwriteInv.length} Invoice(s) already exist!`,
          warningMsg,
          buttonOptions,
        );

        if (continueSave == 'Cancel') {
          this.$fullscreenLoading();
          return;
        }
        const dup = existingInv.map((i) => i['Invoice No']);
        allData = allData.filter((d) => !dup.includes(d['arInvNo']));
        this.uploadedData = this.uploadedData.map((d) => {
          return dup.includes(d['Invoice #']) ?
              Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/warning.png' }, d)
            : d;
        });
      }

      for (const data of allData) {
        this.$fullscreenLoading(`Saving Invoice ${data['arInvNo']}`);
        if (data['arInvNo'].substring(0, 2) == 'CN') {
          await this.saveCN(data);
        } else {
          await this.saveInvoice(data);
        }
      }
      this.showImportDataTable = false;
      this.$nextTick(() => {
        this.showImportDataTable = true;
        this.showSuccessInv = true;
      });
      this.$fullscreenLoading();
    },

    groupImportInvoice: function () {
      // group data by invoice for easier mapping based on document type (in, cn, dn)
      let distinctInvNo = this.uploadedData.map((d) => d[this.invTableHeader]);
      distinctInvNo = [...new Set(distinctInvNo)];
      const grouped = [];
      distinctInvNo.forEach((inv) => {
        const set = this.uploadedData.filter((d) => d[this.invTableHeader] == inv && d['Item Number'] != '');
        const invHeader = {
          // hard code for myob first
          arInvNo: inv,
          coName: set[0]['Co./Last Name'],
          coFName: set[0]['First Name'],
          coAddr1: set[0]['Addr 1 - Line 1'],
          coAddr2: set[0]['- Line 2'],
          coAddr3: set[0]['- Line 3'],
          coAddr4: set[0]['- Line 4'],
          country: set[0]['Destination Country'],
          K2: set[0]['K2 #'],
          Inclusive: set[0]['Inclusive'],
          invDate: typeof(set[0]['Date']) == 'object' ? new Date(set[0]['Date']).formatString('dd/MM/yyyy') : set[0]['Date'],
          remarks: [...new Set(set.map((s) => s['Journal Memo']))].join('\n'),
          customerPO: set[0]['Customer PO'],
          shipVia: set[0]['Ship Via'],
          deliveryStatus: set[0]['Delivery Status'],
          amtPaid: set[0]['Amount Paid'].toString().replace(/[^0-9.]/g, ''),
          paymentMethod: set[0]['Payment Method'],
          paymentNotes: set[0]['Payment Notes'],
          nameOnCard: set[0]['Name On Card'],
          cardNo: set[0]['Card Number'],
          cardExpiryDate: set[0]['Expiry Date'],
          authoCode: set[0]['Authorisation Code'],
          accNum: set[0]['Account Number'],
          accName: set[0]['Account Name'],
          chequeNo: set[0]['Cheque Number'],
          category: set[0]['Category'],
          locationID: set[0]['Location ID'],
          tariffCode: set[0]['Tariff Code'],
          cardID: set[0]['Card ID'],
          recordID: set[0]['Record ID'],
        };
        const invDetails = [];
        set.forEach((s) => {
          const details = {
            itemNo: s['Item Number'],
            qty: Number(s['Quantity'].toString().replaceAll(',', '')),
            desc: s['Description'],
            uPrice: Number(s['Price'].toString().replace(/[^0-9.]/g, '')),
            uPriceInclTax: Number(s['Inc-Tax Price'].toString().replace(/[^0-9.]/g, '')),
            discount: s['Discount'].toString().replace('%', ''),
            total: Number(s['Total'].toString().replace(/[^0-9.]/g, '')),
            totalInclTax: Number(s['Inc-Tax Total'].toString().replace(/[^0-9.]/g, '')),
            job: s['Job'],
            remarks: s['Journal Memo'],
            remarks2: s['Comment'],
            salesPersonFirstName: s['Salesperson First Name'],
            salesPersonLastName: s['Salesperson Last Name'],
            shippingDate: s['Shipping Date'],
            ref: s['Referral Source'],
            taxCode: s['Tax Code'],
            nonTaxAmt: Number(s['Non-Tax Amount'].toString().replace(/[^0-9.]/g, '')),
            taxAmt: Number(s['Tax Amount'].toString().replace(/[^0-9.]/g, '')),
            freightAmt: s['Freight Amount'].toString().replace(/[^0-9.]/g, ''),
            frieghtAmtInclTax: s['Inc-Tax Freight Amount'].toString().replace(/[^0-9.]/g, ''),
            freightTaxCode: s['Freight Tax Code'],
            freightAmtExclTax: s['Freight Non-Tax Amount'].toString().replace(/[^0-9.]/g, ''),
            freightTaxAmt: s['Freight Tax Amount'].toString().replace(/[^0-9.]/g, ''),
            salesStatus: s['Sale Status'],
            currency: s['Currency Code'],
            exchRate: s['Exchange Rate'],
            termsPaymentDue: s['Terms - Payment is Due'],
            termsDiscDays: s['- Discount Days'],
            termsBalDue: s['- Balance Due Days'],
            termsDisc: s['- % Discount'],
            termsMnthChrg: s['- % Monthly Charge'],
          };
          invDetails.push(details);
        });
        invHeader['details'] = invDetails;
        grouped.push(invHeader);
      });
      return grouped;
    },

    saveInvoice: async function (data) {
      const tempDetails = [];
      const itemNo = data['details'].map((d) => d['itemNo']);
      const chargeCodes = await this.$apiCallV2('/BatchImport/GetChargesClass', { chargecodes: itemNo.join(',') });
      if (Array.from(new Set(itemNo)).length != chargeCodes.length) {
        let missingCharges = [];
        if (chargeCodes.length == 0) {
          missingCharges = itemNo;
        } else {
          missingCharges = itemNo.filter((i) => !chargeCodes.some((c) => c.ChargeCode === i));
        }

        this.showMessage(
          'import error',
          `There was an error saving invoice ${data['arInvNo']}: Charges Codes ${missingCharges.join(',')} not found.`,
        );
        this.uploadedData = this.uploadedData.map((d) => {
          return d['Invoice #'] == data['arInvNo'] ?
              Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/error.png' }, d)
            : d;
        });
        this.$fullscreenLoading();
        return;
      }

      for (const [index, item] of data['details'].entries()) {
        const negQty = item['qty'] < 0;
        const taxRate = item['taxCode'] == this.sstCode6 ? 6 : 0;
        tempDetails.push({
          transactioncode: '',
          seqno: index + 1,
          jobno: item['job'] ?? '',
          servicetax: taxRate,
          buying: '0.00',
          taxableamt: (Number(item['totalInclTax']) - Number(item['total'])).toFixed(2),
          selling: (item['totalInclTax'] * (negQty ? -1 : 1)).toFixed(2),
          markup: (item['total'] * (negQty ? -1 : 1)).toFixed(2),
          remarks1: '',
          type: '',
          remarks2: item['remarks2'],
          remarks3: '',
          fbuying: '0.00',
          ftaxableamt: ((Number(item['totalInclTax']) - Number(item['total'])) * (negQty ? -1 : 1)).toFixed(2),
          fselling: (item['totalInclTax'] * (negQty ? -1 : 1)).toFixed(2),
          fmarkup: (item['total'] * (negQty ? -1 : 1)).toFixed(2),
          sellingrate: Number(item['exchRate']) > 0 ? Number(item['exchRate']).toFixed(4) : '1.0000',
          sellingcurrency: item['currency'] || 'MYR',
          cnno: '',
          chargescode: item['itemNo'],
          remarks4: '',
          tabsource: 'D',
          chargesdesc: item['desc'] || chargeCodes.find((c) => c['ChargeCode'] == item['itemNo'])?.Description,
          action: 'N',
          contno: '',
          jobtxnid: '',
          txnid: '',
          uom: '',
          qty: item['qty'] * (negQty ? -1 : 1),
          adjtype: '',
          billingfmt: '',
          rot: '',
          buyingrate: Number(item['exchRate']) > 0 ? Number(item['exchRate']).toFixed(4) : '1.0000',
          buyingcurrency: item['currency'] || 'MYR',
          buyingunitprice: '0.00',
          fbuyingunitprice: '0.00',
          sellingunitprice: (item['uPrice'] * (negQty ? -1 : 1)).toFixed(2),
          fsellingunitprice: (item['uPrice'] * (negQty ? -1 : 1)).toFixed(2),
          costcode: '',
          orinetamt: '',
          taxcode: item['taxCode']?.toUpperCase() != 'ESV6' ? '' : 'ESV6', // Only make sure [ArInvoiceDetails.Tax] = 6. The only tax code we have for SST is "ESV6" for now
          gsttaxcode: item['taxCode']?.toUpperCase() != 'ESV6' ? '' : 'ESV6',
          gsttaxrate: '',
          gsttaxableamt: '0.00',
          gstftaxableamt: '0.00',
          notes: item['remarks'] || '',
          sellingtotal: (item['total'] * (negQty ? -1 : 1)).toFixed(2),
          fsellingtotal: (item['total'] * (negQty ? -1 : 1)).toFixed(2),
          payeetype: '',
          payeecode: '',
        });
      }

      const detailsCurr = Array.from(new Set(tempDetails.map((data) => data['sellingcurrency'])));
      const headerCurr =
        detailsCurr.length == 0 ? ''
        : detailsCurr.length > 1 ? 'MYR'
        : detailsCurr[0];
      const detailsExchRate = tempDetails.map((data) => Number(data['sellingrate']));
      const headerExchRate =
        detailsExchRate.length == 0 ?
          '1.0000'
        : Number(detailsExchRate.reduce((accumulator, currentValue) => accumulator + currentValue, 0)) /
          Number(detailsExchRate.length);
      let total = tempDetails.map((data) => Number(data['selling']));
      total = total.reduce((accumulator, currentValue) => Number(accumulator) + Number(currentValue), 0);

      // Check if is disbursement invoice
      const isDisbursement = chargeCodes.map((c) => c['isDisbursement']).every((d) => d == '1');

      // Find disbursement jobtype id
      const dis = this.jobTypes['invoice'].find((i) => i.Description.toLowerCase().includes('disbursement'));
      const dataToImport = {
        action: 'N',
        system: 'FINAR',
        invjobtype:
          data['arInvNo'].toUpperCase().substring(0, 2) == 'DN' ? '0'
          : isDisbursement && dis ? dis['JobTypeID']
          : '0',
        billcust: '',
        billcustname: data['coName'],
        credittype: '',
        creditterm: '',
        invdate: this.formatDateyyToyyyy(data['invDate']),
        currency: headerCurr,
        rate:
          headerCurr == 'MYR' ? '1.0000'
          : Number(headerExchRate) > 0 ? Number(headerExchRate).toFixed(4)
          : '1.0000',
        advancecollection: '0',
        finalbill: '0',
        arinvno: data['arInvNo'],
        remark1: data['remarks'],
        remark2: '',
        remark3: '',
        total: Number(total).toFixed(2),
        txntype: data['arInvNo'].toUpperCase().substring(0, 2) == 'DN' ? '1' : '0',
        doctype: data['arInvNo'].toUpperCase().substring(0, 2) == 'DN' ? 'D' : 'I',
        mknotfinal: '',
        gstinvoice: '0',
        invoicecharges: tempDetails,
        deliverydetails: {
          companyname: '',
          companytin: '',
          companyregno: '',
          addr1: '',
          addr2: '',
          addr3: '',
          addr4: '',
          postcode: '',
          city: '',
          state: '',
          country: '',
        },
        headerinfojobno: '',
        headerinfocheck: '0',
        jobinfoleft: '',
        jobinforight: '',
        gweight: '',
        measurement: '',
        commodity: '',
        updatejobcheck: '0',
        attachment: [],
        overwrite: '1',
      };

      const result = await this.$apiCallV2('/BatchImport/SaveArInvoice', dataToImport);
      if (result[0]['Results'] != 0) {
        this.showMessage(
          'import error',
          `There was an error saving invoice ${data['arInvNo']}: ${result[0]['ErrMsg']}`,
        );
        this.uploadedData = this.uploadedData.map((d) => {
          return d['Invoice #'] == data['arInvNo'] ?
              Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/error.png' }, d)
            : d;
        });
      } else {
        this.uploadedData = this.uploadedData.filter((ud) => ud['Invoice #'] != data['arInvNo']);
        this.showSuccessInv = false;
        const delIndex = this.importSuccessInv.findIndex((f) => f['Invoice #'] == data['arInvNo']); //delete existing inv no in the importSucessInv table
        if (delIndex != -1) {
          this.importSuccessInv.splice(delIndex, 1);
        }
        this.importSuccessInv.push({
          'Invoice #': data['arInvNo'],
          'Invoice Date': dataToImport['invdate'],
          'Total Amount': this.$currencyFormat(total),
        });
      }
    },

    saveCN: async function (cdata) {
      const negQty = Number(cdata['qty']) < 0;

      const itemNo = cdata['details'].map((d) => d['itemNo']);
      const chargeCodes = await this.$apiCallV2('/BatchImport/GetChargesClass', { chargecodes: itemNo.join(',') });
      if (Array.from(new Set(itemNo)).length != chargeCodes.length) {
        let missingCharges = [];
        if (chargeCodes.length == 0) {
          missingCharges = itemNo;
        } else {
          missingCharges = itemNo.filter((i) => !chargeCodes.some((c) => c.ChargeCode === i));
          missingCharges = Array.from(new Set(missingCharges));
        }

        this.showMessage(
          'import error',
          `There was an error saving credit note ${cdata['arInvNo']}: Charges Codes ${missingCharges.join(',')} not found.`,
        );
        this.uploadedData = this.uploadedData.map((d) => {
          return d['Invoice #'] == cdata['arInvNo'] ?
              Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/error.png' }, d)
            : d;
        });
        this.$fullscreenLoading();
        return;
      }

      const tempDetails = cdata['details'].map((item, index) => {
        return {
          action: 'N',
          seqno: index + 1,
          inv_no: '',
          job_no: '',
          chargescode: item['itemNo'],
          tax: item['taxCode'] == this.sstCode6 ? '6' : '0',
          amount: (Number(item['total']) * (negQty ? -1 : 1)).toFixed(2),
          famount: (Number(item['total']) * (negQty ? -1 : 1)).toFixed(2),
          taxableamt: ((Number(item['totalInclTax']) - Number(item['total'])) * (negQty ? -1 : 1)).toFixed(2),
          ftaxableamt: ((Number(item['totalInclTax']) - Number(item['total'])) * (negQty ? -1 : 1)).toFixed(2),
          netamt: (item['totalInclTax'] * (negQty ? -1 : 1)).toFixed(2),
          fnetamt: (item['totalInclTax'] * (negQty ? -1 : 1)).toFixed(2),
          description: chargeCodes.find((c) => c['ChargeCode'] == item['itemNo'])?.Description,
          detail_remark: item['desc'],
          detail_remark2: '',
          detail_remark3: '',
          consignementno: '',
          invtxnid: '',
          costcode: '',
          gst2taxcode: item['taxCode']?.toUpperCase() != 'ESV6' ? '' : 'ESV6',
          gst2taxrate: item['taxCode'] == this.sstCode6 ? '6' : '0',
          taxable_amountgst2: '',
          ftaxable_amountgst2: '',
        };
      });

      const detailsCurr = Array.from(new Set(cdata['details'].map((data) => data['currency'])));
      const headerCurr =
        detailsCurr.length == 0 ? ''
        : detailsCurr.length > 1 ? 'MYR'
        : detailsCurr[0];
      const detailsExchRate = cdata['details'].map((data) => Number(data['exchRate']) || '1.0000');
      const headerExchRate =
        detailsExchRate.length == 0 ?
          '1.0000'
        : Number(detailsExchRate.reduce((accumulator, currentValue) => accumulator + currentValue, 0)) /
          Number(detailsExchRate.length);

      let total = tempDetails.map((data) => Number(data['netamt']));
      total = total.reduce((accumulator, currentValue) => Number(accumulator) + Number(currentValue), 0);

      const dataToImport = {
        action: 'N',
        billcu_no: '', //map custno based on cust name
        billcu_name: cdata['coName'],
        remarks: cdata['remarks'],
        remarks2: '',
        remarks3: '',
        rate:
          headerCurr == 'MYR' ? '1.0000'
          : Number(headerExchRate) > 0 ? Number(headerExchRate).toFixed(4)
          : '1.0000',
        currency: headerCurr,
        txntype: '0', // 0-normal cn, 1-temp, 2-contra/adjustment
        reinstate: '0',
        date: this.formatDateyyToyyyy(cdata['invDate']),
        creditno: cdata['arInvNo'],
        credit_amount: Number(total).toFixed(2),
        fcredit_amount: Number(total).toFixed(2),
        credit_balance: Number(total).toFixed(2),
        fcredit_balance: Number(total).toFixed(2),
        remarks4: '',
        job_no: '',
        adjentry: '0',
        reqposting: '0',
        authorized: 'A',
        contrano: '',
        jobconfig: '0', // All Job Types
        updatejobcheck: '',
        headerinfocheck: '0',
        jobinfoleft: '',
        jobinforight: '',
        details: tempDetails,
        settlement: [],
        additionalinfo: [
          {
            companyname: '',
            companytin: '',
            companyregno: '',
            addr1: '',
            addr2: '',
            addr3: '',
            addr4: '',
            postcode: '',
            city: '',
            state: '',
            country: '',
          },
        ],
        headerinfojobno: '',
        gweight: '',
        measurement: '',
        commodity: '',
        attachment: [],
        overwrite: '1',
      };

      const result = await this.$apiCallV2('/BatchImport/SaveCreditNote', dataToImport);
      if (result[0]['Results'] != 0) {
        this.showMessage(
          'import error',
          `There was an error saving credit note ${cdata['arInvNo']}: ${result[0]['ErrMsg']}`,
        );
        this.uploadedData = this.uploadedData.map((d) => {
          return d['Invoice #'] == cdata['arInvNo'] ?
              Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/error.png' }, d)
            : d;
        });
      } else {
        this.uploadedData = this.uploadedData.filter((ud) => ud['Invoice #'] != cdata['arInvNo']);
        this.showSuccessInv = false;
        const delIndex = this.importSuccessInv.findIndex((f) => f['Invoice #'] == cdata['arInvNo']); //delete existing inv no in the importSucessInv table
        if (delIndex != -1) {
          this.importSuccessInv.splice(delIndex, 1);
        }
        this.importSuccessInv.push({
          'Invoice #': cdata['arInvNo'],
          'Invoice Date': dataToImport['date'],
          'Total Amount': this.$currencyFormat(total),
        });
      }
      if (this.uploadedData.length == 0) {
        this.showMessage('success', 'All invoices have been imported successfully');
      }
    },

    formatDateyyToyyyy: function (date) {
      let [day, month, year] = ['', '', ''];
      if (date.includes('-')) {
        [day, month, year] = date.split('-');
      } else if (date.includes('/')) {
        [day, month, year] = date.split('/');
      }
      const fullYear = year.length < 4 ? parseInt(year) + 2000 : parseInt(year);
      return new Date(fullYear, parseInt(month) - 1, day).formatString('yyyy-MM-dd');
    },

    importSuccessButtonClicked: function (btn, data) {
      const action =
        btn == 'Edit' ? 'E'
        : btn == 'View' ? 'V'
        : '';
      let page = '';
      switch (data['Invoice #'].substring(0, 2)) {
        case 'CN':
          page = 'Credit%20Note';
          break;

        case 'DN':
          page = 'Debit%20Note';
          break;

        default:
          page = 'Invoice';
          break;
      }
      const quotationPageLink = this.$router.resolve(
        `/Home/${this.$route.params['user']}/FINAR/${page}?action=${action}&invno=${data['Invoice #']}&date=${data['Invoice Date']}`,
      );
      window.open(quotationPageLink.href, '_blank');
    },
  },
  async mounted() {
    this.$fullscreenLoading('Getting Invoice Types');
    this.jobTypes['invoice'] = await this.$apiCallV2('/BatchImport/GetJobTypeConfig', { doctype: 'I' });
    this.jobTypes['debit'] = await this.$apiCallV2('/BatchImport/GetJobTypeConfig', { doctype: 'D' });
    this.jobTypes['credit'] = await this.$apiCallV2('/BatchImport/GetJobTypeConfig', { doctype: 'C' });

    this.$fullscreenLoading('Getting Settings System');
    this.sstCode6 =
      (await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6GImpInv_SSTCode'))?.[0]?.SettingsValue ?? '';
    this.$fullscreenLoading();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.import-main {
  padding: 10px 45px;
  font-size: 12px;
  width: 100%;
  display: flex;
  flex-direction: column;

  > .top-section {
    background-color: white;
    border-radius: 5px;
    padding: 5px;
    height: max-content;

    > .file-input {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;

      > :first-child {
        max-width: calc(40% - 88px);
      }

      > :not(:first-child) {
        margin-left: 5px;
      }

      > button {
        white-space: nowrap;
      }

      .import-button {
        margin-right: 10px;
        height: fit-content;
        margin-left: 20px;
        width: max-content;
      }

      > .message-container {
        width: 100%;
        min-height: 75px;
        max-height: 75px;
        overflow: auto;
        padding: 5px;
        flex-grow: 1;
      }
    }
  }

  > .bottom-section {
    display: flex;
    align-items: center;
    margin-top: 5px;
    width: 100%;
    height: 90%;

    > .table-container {
      width: 50%;
      height: 100%;
      padding: 5px;
      background-color: white;
      border-radius: 5px;
    }
  }
}
</style>
